import { useState } from "react";
import { Badge } from "@heroui/badge";
import { Snippet } from "@heroui/snippet";
import { Bell, ChevronDown, ChevronUp } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";

interface NotificationItemProps {
  notification: {
    action: string;
    email: string;
    fullName: string;
    role: string;
    seen: boolean;
    timestamp: string;
    message: string;
    farmerID?: string;
    supervisorID?: string;
    animalID?: string;
    farmID?: string;
    changes?: Record<string, string>;
    animalDetails?: {
      name: string;
      breed: string;
      gender: string;
      dateOfBirth: string;
      initialWeight: string;
      healthStatus: string;
    };
    dgrValue?: string;
  };
}

export function NotificationItem({ notification }: NotificationItemProps) {
  const [expanded, setExpanded] = useState(false);

  const getInitials = (name: string) =>
    name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();

  const getActionColor = (action: string) => {
    const actionColors: Record<string, string> = {
      "Farmer Registered": "bg-green-100 text-green-800",
      "Animal Registered": "bg-blue-100 text-blue-800",
      "Maternal Phase Added": "bg-purple-100 text-purple-800",
      "Weaning Phase Added": "bg-yellow-100 text-yellow-800",
      "Yearling Phase Added": "bg-orange-100 text-orange-800",
      "Animal Data Update Requested": "bg-red-100 text-red-800",
      "DGR Added by Supervisor": "bg-indigo-100 text-indigo-800",
    };

    return actionColors[action] || "bg-gray-100 text-gray-800";
  };

  const renderDetails = () => {
    if (!expanded) return null;

    const details = [
      { label: "Farmer ID", value: notification.farmerID },
      { label: "Supervisor ID", value: notification.supervisorID },
      { label: "Animal ID", value: notification.animalID },
      { label: "Farm ID", value: notification.farmID },
      { label: "DGR Value", value: notification.dgrValue },
    ];

    return (
      <div className="mt-2 text-xs space-y-1">
        {details
          .filter((d) => d.value)
          .map((d) => (
            <p key={d.label}>
              <span className="font-semibold">{d.label}:</span>
              <Snippet color={"secondary"} size="sm" symbol="#" variant="flat">
                {d.value}
              </Snippet>
            </p>
          ))}
        {notification.changes && (
          <div>
            <p className="font-semibold mt-1">Changes:</p>
            <ul className="list-disc list-inside">
              {Object.entries(notification.changes).map(([key, value]) => (
                <li key={key}>
                  {key}: {value}
                </li>
              ))}
            </ul>
          </div>
        )}
        {notification.animalDetails && (
          <div>
            <p className="font-semibold mt-1">Animal Details:</p>
            <ul className="list-disc list-inside">
              {Object.entries(notification.animalDetails).map(
                ([key, value]) => (
                  <li key={key}>
                    {key}: {value}
                  </li>
                ),
              )}
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <Card className={`mb-2 ${notification.seen ? "opacity-70" : ""}`}>
      <CardContent className="p-2">
        <div className="flex items-start space-x-2">
          <Avatar className="w-8 h-8">
            <AvatarFallback className="text-xs">
              {getInitials(notification.fullName)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex justify-between items-start">
              <div>
                <p className="font-semibold text-sm truncate">
                  {notification.fullName}
                </p>
                <p className="text-xs text-muted-foreground">
                  {notification.role}
                </p>
              </div>
              <Badge
                className={`text-xs px-1 py-0 ${getActionColor(notification.action)}`}
              >
                {notification.action}
              </Badge>
            </div>
            <p className="text-xs mt-1 line-clamp-2">{notification.message}</p>
            {renderDetails()}
            <div className="flex justify-between items-center mt-2 text-xs">
              <p className="text-muted-foreground">
                {/*{format(new Date(notification.timestamp), 'MMM d, HH:mm')}*/}
              </p>
              <div className="flex space-x-1">
                <Button
                  className="h-6 px-1"
                  size="sm"
                  variant="ghost"
                  onClick={() => setExpanded(!expanded)}
                >
                  {expanded ? (
                    <ChevronUp className="h-3 w-3" />
                  ) : (
                    <ChevronDown className="h-3 w-3" />
                  )}
                  <span className="ml-1">{expanded ? "Less" : "More"}</span>
                </Button>
                {!notification.seen && (
                  <Button className="h-6 px-1" size="sm" variant="ghost">
                    <Bell className="h-3 w-3" />
                    <span className="sr-only">Mark as Read</span>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
